var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import { t } from '@lingui/core/macro';
import { Falcon } from '@pocketlaw/tetris/macro';
import { Bold, CustomMentions, DecoupledEditor, Essentials, Italic, List, Paragraph, } from 'shared/domains/pleditor@next';
import { RichTextEditorDocument } from './RichTextEditor.gql';
import { ToolbarButton } from './ToolbarButton';
import { EditorContainer, EditorStyles, ToolbarContainer, ToolbarDivider } from './styled';
/**
 *
 * The RichTextEditor component is a wrapper around the Pleditor DecoupledEditor.
 * It provides a set of basic formatting options and mentions for users and teams.
 *
 * It takes Markdown as input and returns Markdown as output.
 *
 */
export function RichTextEditor(props) {
    const { value, placeholder, onChange } = props;
    const [queryMentions] = useLazyQuery(RichTextEditorDocument);
    const [editorRef, setEditorRef] = useState(null);
    const handleReady = (editor) => {
        setEditorRef(editor);
    };
    const handleChange = (event, editor) => {
        onChange(editor.getData());
    };
    const getMentions = (query) => __awaiter(this, void 0, void 0, function* () {
        try {
            const { data } = yield queryMentions();
            const { users = [], teams = [] } = data || {};
            const normalizedUsers = users.map((user) => {
                var _a;
                return ({
                    id: `@${user.id}`,
                    link: `user:${user.id}`,
                    text: (_a = user.name) !== null && _a !== void 0 ? _a : t({ message: 'Deleted user', comment: 'Deleted user' }),
                });
            });
            const normalizedTeams = teams.map((team) => ({
                id: `@${team.id}`,
                link: `team:${team.id}`,
                text: team.name,
            }));
            const items = [...normalizedUsers, ...normalizedTeams];
            return items
                .filter((item) => item.text.toLowerCase().includes(query.toLowerCase()))
                .sort((a, b) => {
                const aName = a.text.toLowerCase();
                const bName = b.text.toLowerCase();
                if (aName < bName) {
                    return -1;
                }
                if (aName > bName) {
                    return 1;
                }
                return 0;
            });
        }
        catch (_a) {
            return [];
        }
    });
    const getType = (type) => {
        switch (type) {
            case 'user':
                return t({ message: 'User', comment: 'Mention type: User' });
            case 'team':
                return t({ message: 'Team', comment: 'Mention type: Team' });
            default:
                return '';
        }
    };
    const config = {
        licenseKey: process.env.PLEDITOR_LICENSE_KEY,
        plugins: [Essentials, Paragraph, Bold, Italic, List, CustomMentions],
        placeholder,
        mention: {
            feeds: [
                {
                    marker: '@',
                    feed: getMentions,
                    minimumCharacters: 0,
                    dropdownLimit: 6,
                    itemRenderer: (item) => {
                        const element = document.createElement('div');
                        element.className = 'custom-mention-item';
                        const nameElement = document.createElement('span');
                        nameElement.className = 'custom-mention-item__name';
                        nameElement.textContent = `${item.text}`;
                        element.appendChild(nameElement);
                        if (!item.link) {
                            return element;
                        }
                        const type = item.link.split(':')[0];
                        const typeElement = document.createElement('span');
                        typeElement.className = 'custom-mention-item__type';
                        typeElement.textContent = getType(type);
                        element.appendChild(typeElement);
                        return element;
                    },
                },
            ],
        },
    };
    return (<EditorContainer>
      {editorRef && (<ToolbarContainer>
          <ToolbarButton commandKey="undo" editor={editorRef} tooltip={t({ message: 'Undo', comment: 'Rich text editor - Undo tooltip' })}>
            <Falcon icon="arrow-rotate-left"/>
          </ToolbarButton>
          <ToolbarButton commandKey="redo" editor={editorRef} tooltip={t({ message: 'Redo', comment: 'Rich text editor - Redo tooltip' })}>
            <Falcon icon="arrow-rotate-right"/>
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton commandKey="bold" editor={editorRef} tooltip={t({ message: 'Bold', comment: 'Rich text editor - Bold tooltip' })}>
            <Falcon icon="bold"/>
          </ToolbarButton>
          <ToolbarButton commandKey="italic" editor={editorRef} tooltip={t({ message: 'Italic', comment: 'Rich text editor - Italic tooltip' })}>
            <Falcon icon="italic"/>
          </ToolbarButton>

          <ToolbarDivider />

          <ToolbarButton commandKey="bulletedList" editor={editorRef} tooltip={t({ message: 'Bulleted list', comment: 'Rich text editor - Bulleted list tooltip' })}>
            <Falcon icon="list-ul"/>
          </ToolbarButton>
          <ToolbarButton commandKey="numberedList" editor={editorRef} tooltip={t({ message: 'Numbered list', comment: 'Rich text editor - Numbered list tooltip' })}>
            <Falcon icon="list-ol"/>
          </ToolbarButton>
        </ToolbarContainer>)}

      <EditorStyles />

      <CKEditor editor={DecoupledEditor} config={config} data={value} onReady={handleReady} onChange={handleChange}/>
    </EditorContainer>);
}
